import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import { useAppDispatch } from 'src/hooks/useAppDispatch';
import { useAppSelector } from 'src/hooks/useAppSelector';
import useAuth from 'src/hooks/useAuth';
import { useAppToolchain } from 'src/hooks/useAppToolchain';
import { useParams } from 'react-router-dom';
import useStyles from 'src/styles/di-theme';
import useLocalStorage from 'src/hooks/useLocalStorage';
import {
	COLUMN_VISIBILITY_INITIAL_STATE,
	COLUMNS_WITHOUT_FILTERING,
	COLUMNS_WITHOUT_SORTING,
	dropdowns,
	SCANLOGS_COLUMN_POSSIBLE_VALUES,
	scanlogsSearchFields,
	COLUMN_DEFINITIONS,
} from './constants';
import { renderCell } from './cellRenderer';
import {
	resetScanlogsState,
	updateDateRange,
	updateFilters,
	updatePage,
	updatePageSize,
	updateSearchTextAndField,
	updateSort,
} from 'src/features/scanlogs/scanLogsSlice';
import {
	exportESScans,
	fetchAggregatedESScans,
	fetchESScans,
} from 'src/features/scanlogs/thunks';
import DIInputCheckboxFilter from 'src/components/Filters/DIInputCheckboxFilter';
import { debounce } from '@material-ui/core';
import { generateFilterChips, parseSearchValue } from './helper';
import { SearchEndAdornment } from './SearchEndAdornment';
import { TopBarActions } from './ScanLogsTopBarActions';

// =======================
// useScanLogsTableLogic
// =======================
export function useScanLogsTableLogic(itemId = ''): Record<string, any> {
	// ------------------------------------------------------------------------
	// State Variables & Custom Hooks
	// ------------------------------------------------------------------------

	const dispatch = useAppDispatch();
	const { auth } = useAuth();
	const { api } = useAppToolchain();
	const params: any = useParams();
	const classes = useStyles();

	// Redux state selectors
	const {
		tableData,
		page,
		pageSize,
		isLoading,
		totalRecords,
		dateRangeStart,
		dateRangeEnd,
		searchText,
		searchField,
		sort,
		sortDir,
		filters,
		filtersAggregation,
	} = useAppSelector((_store) => ({
		tableData: _store.scanlogs.tableData ?? [],
		page: _store.scanlogs.page,
		pageSize: _store.scanlogs.pageSize,
		isLoading: _store.scanlogs.loading,
		totalRecords: _store.scanlogs.totalCount,
		dateRangeStart: _store.scanlogs.dateRangeStart,
		dateRangeEnd: _store.scanlogs.dateRangeEnd,
		searchText: _store.scanlogs.searchText,
		searchField: _store.scanlogs.searchField,
		sort: _store.scanlogs.sort,
		sortDir: _store.scanlogs.sortDir,
		filters: _store.scanlogs.filters,
		filtersAggregation: _store.scanlogs.filtersAggregation,
	}));

	// Local state
	const [selectedFilters, setSelectedFilters] = useState<string[]>(
		itemId.length > 0 ? ['ItemId:' + itemId] : [],
	);
	const [searchValue, setSearchValue] = useState<string>('');
	const [isSearchFocused, setIsSearchFocused] = useState<boolean>(false);
	const [isColumnVisibilityDrawerOpen, setIsColumnVisibilityDrawerOpen] =
		useState<boolean>(false);
	const [sideMenuSearchValue, setSideMenuSearchValue] = useState<string>('');
	const [sorting, setSorting] = useState<
		Array<{ id: string; desc: boolean }>
	>(() =>
		sort
			? [{ id: sort, desc: sortDir === 'desc' }]
			: [{ id: 'scanDate', desc: true }],
	);
	const [datePickerError, setDatePickerError] = useState<boolean>(false);
	const [datePickerErrorMessage, setDatePickerErrorMessage] =
		useState<string>('');

	// Persistent state for column visibility
	const [columnVisibility, setColumnVisibility] = useLocalStorage(
		'scanlogsColumnVisibility',
		COLUMN_VISIBILITY_INITIAL_STATE,
	);

	// ------------------------------------------------------------------------
	// Memoized Values
	// ------------------------------------------------------------------------

	// Date range as Luxon DateTime objects
	const dateRange = useMemo(() => {
		const start = dateRangeStart
			? DateTime.fromISO(dateRangeStart).toLocal()
			: null;
		const end = dateRangeEnd
			? DateTime.fromISO(dateRangeEnd).toLocal()
			: null;
		return [start, end] as [DateTime | null, DateTime | null];
	}, [dateRangeStart, dateRangeEnd]);

	// Filtered dropdowns for side menu
	const sideMenuDropdowns = useMemo(
		() =>
			dropdowns.map((dropdown) => {
				const items = dropdown.items.filter((item) =>
					item.label
						.toLowerCase()
						.includes(sideMenuSearchValue.toLowerCase()),
				);
				return { ...dropdown, items };
			}),
		[dropdowns, sideMenuSearchValue],
	);

	// Debounced dispatch for search field
	const debouncedDispatch = useMemo(
		() =>
			debounce((field, text) => {
				dispatch(updateSearchTextAndField({ text, field }));
				dispatch(updatePage(1));
			}, 500),
		[dispatch],
	);

	// Search input props for focus/blur
	const searchInputProps = useMemo(
		() => ({
			onFocus: () => setIsSearchFocused(true),
			onBlur: () => setIsSearchFocused(false),
		}),
		[],
	);

	// Table columns definition
	const columns = useMemo(
		() =>
			COLUMN_DEFINITIONS.map(({ accessorKey, header }) => ({
				accessorKey,
				header,
				enableColumnActions: false,
				Cell: ({ cell, row }) => renderCell(accessorKey, cell, row),
				enableSorting:
					COLUMNS_WITHOUT_SORTING.indexOf(accessorKey) === -1,
				sortDescFirst: false,
				Filter: () => getColumnFilter(accessorKey),
				enableColumnFilter:
					COLUMNS_WITHOUT_FILTERING.indexOf(accessorKey) === -1,
			})),
		[filters, tableData, filtersAggregation],
	);

	// ------------------------------------------------------------------------
	// useCallback Handlers
	// ------------------------------------------------------------------------

	// Handles column filter changes
	const onColumnFilterChange = useCallback(
		(columnFilter) => {
			const newFilters = filters?.filter(
				(filter) => filter.fieldName !== columnFilter.fieldName,
			);
			newFilters.push(columnFilter);
			dispatch(updateFilters(newFilters));
			dispatch(updatePage(1));
		},
		[dispatch, filters],
	);

	// Removes a column filter
	const handleRemoveColumnFilter = useCallback(
		(columnId: string) => {
			dispatch(
				updateFilters(
					filters?.filter(
						(columnFilter) => columnFilter.fieldName !== columnId,
					),
				),
			);
			dispatch(updatePage(1));
		},
		[filters, dispatch],
	);

	// Clears all filters
	const handleClearAllFilters = useCallback(() => {
		dispatch(updateFilters([]));
		dispatch(updatePage(1));
	}, [dispatch]);

	// Returns the filter UI for a column
	const getColumnFilter = useCallback(
		(accessorKey: string) => {
			if (!accessorKey) return null;
			const { type, possibleValues } =
				SCANLOGS_COLUMN_POSSIBLE_VALUES[accessorKey];

			const columnFilter = filters?.find(
				(filter) => filter.fieldName === accessorKey,
			);

			// Build optionCounts for filter checkboxes
			const optionCounts: Record<string, number> = {};
			if (possibleValues) {
				Object.values(possibleValues).forEach((value) => {
					optionCounts[value] = 0;
				});
				if (filtersAggregation && filtersAggregation[accessorKey]) {
					filtersAggregation[accessorKey].forEach(
						(item: { key: string; count: number }) => {
							optionCounts[item.key] = item.count;
						},
					);
				}
			}

			switch (type) {
				case 'keyword':
					return possibleValues ? (
						<DIInputCheckboxFilter
							checkboxOptions={Object.entries(possibleValues).map(
								([key, value]) => ({
									value: value,
									label: value,
								}),
							)}
							columnId={accessorKey}
							setColumnFilter={onColumnFilterChange}
							columnFilter={columnFilter}
							showSelectAll={false}
							handleRemoveColumnFilter={handleRemoveColumnFilter}
							optionCounts={optionCounts}
						/>
					) : null;
				default:
					return null;
			}
		},
		[
			filters,
			filtersAggregation,
			onColumnFilterChange,
			handleRemoveColumnFilter,
		],
	);

	// Handles date range changes
	const handleDateRangeChange = useCallback(
		(dateRange: [DateTime | null, DateTime | null]) => {
			const [start, end] = dateRange;
			const currentDay = DateTime.local().endOf('day');
			const last90Days = DateTime.local()
				.minus({ days: 90 })
				.startOf('day');

			if (
				!start?.isValid ||
				!end?.isValid ||
				start > end ||
				start > currentDay ||
				start < last90Days ||
				end > currentDay ||
				end < last90Days
			) {
				setDatePickerError(true);
				setDatePickerErrorMessage('Please select valid date');
				return;
			}

			setDatePickerError(false);
			setDatePickerErrorMessage('');

			dispatch(
				updateDateRange({
					start: start.startOf('day').toUTC().toISO(),
					end: end.endOf('day').toUTC().toISO(),
				}),
			);
			dispatch(updatePage(1));
		},
		[dispatch],
	);

	// Handles search field changes
	const handleSearchFieldChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const val = e.target.value;
			const { field, text } = parseSearchValue(val, scanlogsSearchFields);

			setSearchValue(val);

			if (field !== searchField || text !== searchText) {
				debouncedDispatch(field, text);
			}
		},
		[searchField, searchText, debouncedDispatch],
	);

	// Handles pagination change
	const handlePaginationChange = useCallback(
		(event, value) => {
			dispatch(updatePage(value));
		},
		[dispatch],
	);

	// Handles items per page change
	const handleOnClickItemsPerPageOption = useCallback(
		(itemsPerPageValue: number) => {
			dispatch(updatePageSize(itemsPerPageValue));
		},
		[dispatch],
	);

	// Handles column visibility checkbox change
	const handleSideMenuCheckboxChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const { value, checked } = event.target;
			setColumnVisibility({
				...columnVisibility,
				[value]: checked,
			});
		},
		[columnVisibility, setColumnVisibility],
	);

	// Handles file download
	const downloadFile = useCallback(() => {
		dispatch(
			exportESScans({
				api,
				id: auth?.accountDetails?.account?.id,
				startDate: dateRangeStart,
				endDate: dateRangeEnd,
				searchText,
				searchField,
				filters: selectedFilters,
				sort,
				sortDir,
			}),
		);
	}, [
		dispatch,
		api,
		auth?.accountDetails?.account?.id,
		dateRangeStart,
		dateRangeEnd,
		searchText,
		searchField,
		sort,
		sortDir,
		selectedFilters,
	]);

	// Handles refresh logic for date range
	const handleRefresh = useCallback(() => {
		const [start, end] = dateRange;
		let newEnd = end;
		if (end && end.hasSame(DateTime.local(), 'day')) {
			newEnd = DateTime.local();
		} else if (end) {
			newEnd = end.endOf('day');
		}
		dispatch(
			updateDateRange({
				start: start?.startOf('day').toUTC().toISO(),
				end: newEnd?.toUTC().toISO(),
			}),
		);
	}, [dateRange, dispatch]);

	// Handles clearing the search field
	const handleClearSearch = useCallback(() => {
		setSearchValue('');
		dispatch(updateSearchTextAndField({ text: '', field: '' }));
	}, [dispatch]);

	// ------------------------------------------------------------------------
	// Effects
	// ------------------------------------------------------------------------

	// Compute filter strings from Redux filters and optional itemId
	const getComputedFilters = useCallback(() => {
		const computedFilters = filters.map(
			(filter) =>
				`${COLUMN_DEFINITIONS.find((col) => col.accessorKey === filter.fieldName)?.header}: ${filter.value}`,
		);
		if (itemId.length > 0) {
			const itemIdFilter = `ItemId:${itemId}`;
			if (!computedFilters.includes(itemIdFilter)) {
				computedFilters.push(itemIdFilter);
			}
		}
		return computedFilters;
	}, [filters, itemId]);

	// Fetch scan logs data when dependencies change
	useEffect(() => {
		dispatch(
			fetchESScans({
				api,
				id: auth?.accountDetails?.account?.id,
				startDate: dateRangeStart,
				endDate: dateRangeEnd,
				searchText,
				searchField,
				filters: getComputedFilters(),
				page,
				pageSize,
				sort,
				sortDir,
			}),
		);
	}, [
		auth.accountDetails.account.id,
		searchText,
		searchField,
		filters,
		page,
		pageSize,
		dateRangeStart,
		dateRangeEnd,
		sort,
		sortDir,
	]);

	// Fetch aggregated scan logs data when dependencies change
	useEffect(() => {
		dispatch(
			fetchAggregatedESScans({
				api,
				id: auth?.accountDetails?.account?.id,
				startDate: dateRangeStart,
				endDate: dateRangeEnd,
				searchText,
				searchField,
				filters: getComputedFilters(),
			}),
		);
	}, [
		auth.accountDetails.account.id,
		searchText,
		searchField,
		filters,
		dateRangeStart,
		dateRangeEnd,
	]);

	// Set search value from route params
	useEffect(() => {
		if (params.id) {
			setSearchValue(`file:${params.id}`);
			dispatch(
				updateSearchTextAndField({ text: params.id, field: 'file' }),
			);
		} else {
			setSearchValue('');
			dispatch(updateSearchTextAndField({ text: '', field: '' }));
		}
	}, [params.id, dispatch]);

	// Update selected filters when filters change
	useEffect(() => {
		setSelectedFilters(() => {
			const newSelectedFilters = filters.map(
				(filter) =>
					`${
						COLUMN_DEFINITIONS.find(
							(col) => col.accessorKey === filter.fieldName,
						)?.header
					}: ${filter.value}`,
			);
			// If itemId filter is present, ensure it stays in the selected filters
			if (itemId.length > 0) {
				const itemIdFilter = `ItemId:${itemId}`;
				if (!newSelectedFilters.includes(itemIdFilter)) {
					newSelectedFilters.push(itemIdFilter);
				}
			}
			return newSelectedFilters;
		});
	}, [filters, itemId]);

	// Sync sorting state with Redux
	useEffect(() => {
		if (sorting.length > 0) {
			const { id, desc } = sorting[0];
			if (id !== sort || (desc ? 'desc' : 'asc') !== sortDir) {
				dispatch(
					updateSort({ sort: id, sortDir: desc ? 'desc' : 'asc' }),
				);
			}
		} else if (sort !== 'scanDate' || sortDir !== '') {
			dispatch(updateSort({ sort: 'scanDate', sortDir: '' }));
		}
	}, [sorting, dispatch, sort, sortDir]);

	// Resetting the state of scan logs table when unmounting the component
	useEffect(() => {
		return () => {
			dispatch(resetScanlogsState());
		};
	}, [dispatch]);

	// ------------------------------------------------------------------------
	// Return all logic and state
	// ------------------------------------------------------------------------

	return {
		// State
		tableData,
		page,
		pageSize,
		isLoading,
		totalRecords,
		dateRange,
		dateRangeStart,
		dateRangeEnd,
		searchValue,
		setSearchValue,
		isSearchFocused,
		setIsSearchFocused,
		columnVisibility,
		setColumnVisibility,
		sorting,
		setSorting,
		filters,
		filtersAggregation,
		sideMenuDropdowns,
		isColumnVisibilityDrawerOpen,
		setIsColumnVisibilityDrawerOpen,
		sideMenuSearchValue,
		setSideMenuSearchValue,
		datePickerError,
		datePickerErrorMessage,

		// Handlers
		handleDateRangeChange,
		handleSearchFieldChange,
		handleClearSearch,
		handleRefresh,
		handlePaginationChange,
		handleOnClickItemsPerPageOption,
		handleSideMenuCheckboxChange,
		handleRemoveColumnFilter,
		handleClearAllFilters,
		downloadFile,
		getColumnFilter,

		// Memoized/Helper
		columns,
		generateFilterChips,
		classes,
		TopBarActions,
		SearchEndAdornment,
		searchInputProps,
	};
}
